import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { getRoleDefinitionsQueryOptions } from '@/features/roles/queries';
import { authStore } from '@/services/auth/store';
import { Role as StandardRole } from '@customerio/hydra-permissions/roles';
import { workspacePermissions, type WorkspacePermission } from '@customerio/hydra-permissions';
import type { RolesRoleDefinitionsResponse } from '@customerio/hydra-ui-api';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

type RoleDefinition = {
  id: number;
  name: string;
  workspacePermissions: Set<WorkspacePermission>;
};

type UseRoleDefinitionsResult = {
  /** The account's roles, or null while unresolved and after a failed fetch. */
  data: Record<number, RoleDefinition> | null;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  /** For a caller that renders a retry after a failed fetch. */
  refetch: () => void;
};

// The response types the permission-map values as plain strings. Look each one
// up to get a `WorkspacePermission` back, and drop the ones that aren't.
const workspacePermissionsByName = new Map<string, WorkspacePermission>(
  workspacePermissions.map((permission) => [permission, permission]),
);

export function buildRoleDefinitions(response: RolesRoleDefinitionsResponse) {
  const roleDefinitions: Record<number, RoleDefinition> = {};

  for (const roleDefinition of response.role_definitions) {
    const permissions = new Set<WorkspacePermission>();

    for (const permissionId of roleDefinition.permission_ids) {
      const permission = workspacePermissionsByName.get(response.permissions[permissionId]);
      if (permission != null) {
        permissions.add(permission);
      }
    }

    roleDefinitions[roleDefinition.id] = {
      id: roleDefinition.id,
      name: roleDefinition.name,
      workspacePermissions: permissions,
    };
  }

  return roleDefinitions;
}

/**
 * Callers must handle the unresolved states. A denial read from `data` while the
 * fetch is pending is not an answer, and neither is one read after it failed:
 * both look the same as a role that grants nothing.
 */
export function useRoleDefinitions(): UseRoleDefinitionsResult {
  const auth = useStore(authStore);
  const accountId = auth.account?.id ?? null;

  const rolesQuery = useQuery({
    ...getRoleDefinitionsQueryOptions(accountId ?? 0),
    enabled: accountId != null,
  });

  const data = useMemo(
    () => (rolesQuery.data == null ? null : buildRoleDefinitions(rolesQuery.data)),
    [rolesQuery.data],
  );

  // A disabled query reports `isPending` forever. Signed out there is no account
  // to fetch roles for, so report that as resolved with nothing instead.
  if (accountId == null) {
    return { data: null, isPending: false, isError: false, isSuccess: false, error: null, refetch: () => {} };
  }

  return {
    data,
    isPending: rolesQuery.isPending,
    isError: rolesQuery.isError,
    isSuccess: rolesQuery.isSuccess,
    error: rolesQuery.error,
    refetch: () => {
      void rolesQuery.refetch();
    },
  };
}

/**
 * The user's role in an explicit workspace. Callers on routes without a
 * `/workspaces/<id>` segment (settings, billing) can't use the URL-derived
 * variant below — they resolve a workspace some other way (e.g.
 * `useCurrentWorkspace()`, which falls back to the last/default workspace)
 * and pass its id here.
 */
export function useUserRoleForWorkspace(workspaceId: number | null): { name: string; redacted: boolean } | null {
  const auth = useStore(authStore);
  const userRoles = auth.user?.roles ?? null;

  if (userRoles == null) {
    return null;
  }

  const userWorkspaceRole = userRoles.find((role) => role.workspace_id === workspaceId);

  if (userWorkspaceRole == null) {
    return null;
  }

  return {
    name: userWorkspaceRole.role_name,
    redacted: userWorkspaceRole.role_name.includes('-redact-on'),
  };
}

export function useUserRoleForCurrentWorkspace(): { name: string; redacted: boolean } | null {
  return useUserRoleForWorkspace(getWorkspaceIdFromCurrentUrl());
}

export function isStandardRole(role: string): role is StandardRole {
  return Object.values<string>(StandardRole).includes(role);
}
